import type { Grade } from "../types";

const GRADE_THRESHOLDS: ReadonlyArray<{ grade: Grade; min: number }> = [
  { grade: "S", min: 90 },
  { grade: "A", min: 75 },
  { grade: "B", min: 55 },
  { grade: "C", min: 30 },
];

const REWARD_MULTIPLIERS: Readonly<Record<Grade, number>> = {
  S: 1.2,
  A: 1.0,
  B: 0.8,
  C: 0.55,
  Failed: 0.2,
};

/**
 * Maps a numeric score (0-100) to a letter grade.
 * Canon: S>=90, A>=75, B>=55, C>=30, otherwise Failed.
 */
export function getGrade(score: number): Grade {
  for (const { grade, min } of GRADE_THRESHOLDS) {
    if (score >= min) {
      return grade;
    }
  }
  return "Failed";
}

/**
 * Returns the reward multiplier for a given grade.
 */
export function getRewardMultiplier(grade: Grade): number {
  return REWARD_MULTIPLIERS[grade];
}

export type ScoreBand = "good" | "partial" | "fail";

/**
 * Maps a task score (0-100) to a three-colour band for progress segments,
 * aligned with the grade boundaries: Failed -> fail (red), C -> partial
 * (amber), B/A/S -> good (green).
 */
export function taskScoreBand(score: number): ScoreBand {
  const grade = getGrade(score);
  if (grade === "Failed") {
    return "fail";
  }
  if (grade === "C") {
    return "partial";
  }
  return "good";
}

const SCORED_GRADES: readonly Grade[] = ["S", "A", "B", "C"];

/**
 * Passing grades (S..C, excluding Failed) paired with their reward multiplier,
 * for display of the grade-based reward scaling on contract cards / details.
 */
export function scoredGradeMultipliers(): { grade: Grade; multiplier: number }[] {
  return SCORED_GRADES.map((grade) => ({ grade, multiplier: getRewardMultiplier(grade) }));
}

/**
 * The grade-scaled reward range for a base amount: min at C, max at S (rounded).
 * Excludes skill/tool/urgent bonuses - it shows the grade band only.
 */
export function rewardRange(base: number): { min: number; max: number } {
  return {
    min: Math.round(base * getRewardMultiplier("C")),
    max: Math.round(base * getRewardMultiplier("S")),
  };
}

/**
 * Aggregates per-task scores into a contract score: rounded arithmetic mean.
 * An empty array yields 0.
 */
export function calculateContractScore(taskScores: ReadonlyArray<number>): number {
  if (taskScores.length === 0) {
    return 0;
  }
  const total = taskScores.reduce((sum, score) => sum + score, 0);
  return Math.round(total / taskScores.length);
}

const TRUST_DELTAS: Readonly<Record<Grade, number>> = {
  S: 12,
  A: 8,
  B: 4,
  C: -2,
  Failed: -10,
};

/**
 * Final reward for one resource: base scaled by the grade multiplier and the
 * combined skill/tool bonus, rounded. Order: base -> grade -> (1 + bonuses).
 */
export function calculateFinalReward(
  base: number,
  grade: Grade,
  skillBonus: number,
  toolBonus: number,
): number {
  return Math.round(base * getRewardMultiplier(grade) * (1 + skillBonus + toolBonus));
}

/**
 * The trust change a grade produces, before clamping (canon 11 §4).
 */
export function getTrustDelta(grade: Grade): number {
  return TRUST_DELTAS[grade];
}

/**
 * Applies the grade-based trust delta to a client's trust, clamped to 0-100.
 */
export function applyTrustDelta(trust: number, grade: Grade): number {
  const next = trust + TRUST_DELTAS[grade];
  return Math.min(100, Math.max(0, next));
}

export type BudgetZone = "low" | "medium" | "critical";

/** Warning threshold (fraction of budget) above which spending is "near limit". */
const BUDGET_WARN_RATIO = 0.8;

/**
 * Classifies spend against the client budget into a heat band for the meter:
 * over budget -> critical, near the limit -> medium, otherwise low.
 */
export function budgetZone(totalCost: number, clientBudget: number): BudgetZone {
  if (totalCost > clientBudget) {
    return "critical";
  }
  if (totalCost > clientBudget * BUDGET_WARN_RATIO) {
    return "medium";
  }
  return "low";
}

/**
 * Budget-fit component (0-100): 100 while the selected cost is within budget,
 * then linear down to 0 at twice the budget. Non-positive budget yields 0.
 */
export function budgetFitScore(totalCost: number, clientBudget: number): number {
  if (clientBudget <= 0) {
    return 0;
  }
  if (totalCost <= clientBudget) {
    return 100;
  }
  const overflow = (totalCost - clientBudget) / clientBudget;
  return Math.max(0, Math.round(100 * (1 - overflow)));
}
